import React, { useState, useMemo } from 'react'
import {
  Image,
  Button,
  Pill,
  Filter,
  TableComponents,
  useModalContext,
  GenericError,
} from '@sketch/components'
import { ReactComponent as ExclamationMarkIcon } from '@sketch/icons/exclamation-mark-triangle-64'

import {
  GetIntegrationConnectUrlMutationVariables,
  useGetWorkspaceIntegrationsQuery,
} from '@sketch/gql-types'

import Slack from '@sketch/icons/slack@1x.png'
import SlackRetina from '@sketch/icons/slack@2x.png'

import { ConnectIntegrationModal, IntegrationSettingsModal } from '../../modals'

import {
  Wrapper,
  SearchWrapper,
  Table,
  IntegrationInfo,
  IntegrationIcon,
  IntegrationDetails,
  IntegrationName,
  IntegrationStatus,
  ActionCell,
  Divider,
  EmptyState,
  EmptyCell,
  EmptyRow,
  ErrorWrapper,
  StyledFakeLinkButton,
} from './WorkspaceIntegrations.styles'

import { IntegrationTabs } from './IntegrationTabs'
import { SkeletonRow } from './SkeletonRow'

const { TableRow, TableCell } = TableComponents

// Minimum number of integrations required to show tabs and search
const MIN_INTEGRATIONS_FOR_FILTERS = 5

const TABLE_HEADER = [{ label: 'Integration' }, { label: 'Action' }]

// Skeleton rows for loading state
const SKELETON_ITEMS = [
  { id: 'skeleton-1', name: '', icon: '', icon2x: '', isSkeleton: true },
  { id: 'skeleton-2', name: '', icon: '', icon2x: '', isSkeleton: true },
  { id: 'skeleton-3', name: '', icon: '', icon2x: '', isSkeleton: true },
]

type Integration = {
  id: string
  name: string
  description?: string
  icon: any
  icon2x: any
  isConnected?: boolean
  isDivider?: boolean
  isSkeleton?: boolean
  integrationProvider?: GetIntegrationConnectUrlMutationVariables['provider']
}

const AVAILABLE_INTEGRATIONS: Integration[] = [
  {
    id: 'slack',
    name: 'Slack',
    integrationProvider: 'SLACK',
    icon: Slack,
    icon2x: SlackRetina,
  },
]

type TabType = 'all' | 'enabled' | 'not-configured'

export interface WorkspaceIntegrationsProps {
  workspaceId: string
}

export const WorkspaceIntegrations: React.FC<WorkspaceIntegrationsProps> = ({
  workspaceId,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const { showModal } = useModalContext()

  const { data, loading, error } = useGetWorkspaceIntegrationsQuery({
    variables: { identifier: workspaceId },
  })

  // Merge available integrations with connected integrations from the API
  const allIntegrations = useMemo(() => {
    const connectedIntegrations = data?.workspace.integrations || []

    return AVAILABLE_INTEGRATIONS.map(availableIntegration => {
      const connectedIntegration = connectedIntegrations.find(
        apiIntegration =>
          apiIntegration.integrationProvider ===
          availableIntegration.integrationProvider
      )

      if (connectedIntegration) {
        return {
          ...availableIntegration,
          isConnected: true,
        }
      }

      return {
        ...availableIntegration,
        isConnected: false,
      }
    })
  }, [data?.workspace.integrations])

  const filteredConnectedIntegrations = useMemo(() => {
    let integrations = allIntegrations.filter(int => int.isConnected)

    if (searchQuery) {
      integrations = integrations.filter(integration =>
        integration.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    if (activeTab === 'not-configured') {
      return []
    }

    return integrations
  }, [allIntegrations, searchQuery, activeTab])

  const filteredAvailableIntegrations = useMemo(() => {
    let integrations = allIntegrations.filter(int => !int.isConnected)

    if (searchQuery) {
      integrations = integrations.filter(integration =>
        integration.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    if (activeTab === 'enabled') {
      return []
    }

    return integrations
  }, [allIntegrations, searchQuery, activeTab])

  const filteredIntegrations = useMemo(() => {
    const connected = filteredConnectedIntegrations.map(integration => ({
      ...integration,
      isConnected: true,
    }))
    const available = filteredAvailableIntegrations.map(integration => ({
      ...integration,
      isConnected: false,
    }))

    // Add divider between connected and available if both exist
    if (
      connected.length > 0 &&
      available.length > 0 &&
      activeTab !== 'enabled'
    ) {
      return [
        ...connected,
        { id: 'divider', name: '', icon: '', icon2x: '', isDivider: true },
        ...available,
      ]
    }

    return [...connected, ...available]
  }, [filteredConnectedIntegrations, filteredAvailableIntegrations, activeTab])

  const totalIntegrations = allIntegrations.length
  const enabledCount = filteredConnectedIntegrations.length
  const availableCount = filteredAvailableIntegrations.length

  const showFilters = totalIntegrations >= MIN_INTEGRATIONS_FOR_FILTERS

  const hasResults =
    filteredConnectedIntegrations.length > 0 ||
    filteredAvailableIntegrations.length > 0

  const handleConnect = (integration: Integration) => {
    showModal(ConnectIntegrationModal, {
      integrationName: integration.name,
      integrationProvider: integration.integrationProvider!,
      workspaceId,
    })
  }

  const handleSettings = (integration: Integration) => {
    showModal(IntegrationSettingsModal, {
      integrationName: integration.name,
      integrationProvider: integration.integrationProvider!,
      workspaceId,
    })
  }

  const renderIntegrationRow = (integration: Integration) => {
    // Render skeleton row
    if (integration.isSkeleton) {
      return <SkeletonRow key={integration.id} />
    }

    // Render divider row
    if (integration.isDivider) {
      return (
        <EmptyRow key={integration.id}>
          <EmptyCell colSpan={2}>
            <Divider />
          </EmptyCell>
        </EmptyRow>
      )
    }

    const src = integration.icon
    const src2x = integration.icon2x
    const name = integration.name
    const isConnected =
      !!integration.isConnected && !!integration.integrationProvider

    return (
      <TableRow key={integration.id}>
        <TableCell>
          <IntegrationInfo>
            <IntegrationIcon>
              <Image
                alt={name}
                src={src}
                srcSet={`${src2x} 2x`}
                width="32px"
                height="32px"
              />
            </IntegrationIcon>
            <IntegrationDetails>
              {isConnected ? (
                <IntegrationStatus>
                  <IntegrationName>{name}</IntegrationName>
                  <Pill variant="owner">Enabled</Pill>
                </IntegrationStatus>
              ) : (
                <IntegrationName>{name}</IntegrationName>
              )}
            </IntegrationDetails>
          </IntegrationInfo>
        </TableCell>
        <TableCell>
          <ActionCell>
            {isConnected ? (
              <Button
                variant="secondary-untinted"
                size="32"
                onClick={() => handleSettings(integration)}
              >
                Settings…
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="32"
                onClick={() => handleConnect(integration)}
              >
                Connect…
              </Button>
            )}
          </ActionCell>
        </TableCell>
      </TableRow>
    )
  }

  if (error) {
    return (
      <ErrorWrapper>
        <GenericError
          title="Integrations Unavailable"
          description="There was a problem retrieving the integrations for this workspace."
          icon={<ExclamationMarkIcon />}
          iconSize="medium"
          extra={
            <StyledFakeLinkButton
              variant="primary"
              onClick={() => window.location.reload()}
            >
              Try Again
            </StyledFakeLinkButton>
          }
        />
      </ErrorWrapper>
    )
  }

  return (
    <Wrapper>
      {showFilters && (
        <>
          <IntegrationTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            totalIntegrations={totalIntegrations}
            enabledCount={enabledCount}
            availableCount={availableCount}
          />

          <SearchWrapper>
            <Filter
              data-test-id="integrations-search-filter"
              name="integrations-search-filter"
              placeholder="Search integrations..."
              onChange={setSearchQuery}
              value={searchQuery}
              fullWidth
            />
          </SearchWrapper>
        </>
      )}

      {!hasResults && !loading ? (
        <EmptyState>No integrations found.</EmptyState>
      ) : (
        <Table
          header={TABLE_HEADER}
          items={loading ? SKELETON_ITEMS : filteredIntegrations}
          renderItem={renderIntegrationRow}
          hideHeader
        />
      )}
    </Wrapper>
  )
}
